import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IndexedDBInterface } from './interface/indexedDB.interface';
import { environment } from 'src/environments/environment';
import { objectStoreName } from './common/objectStoreName';

@Injectable({
  providedIn: 'root',
})
export class LoanOfficerIndexedDBService implements IndexedDBInterface {
  objectStoreName = objectStoreName.loanOfficerStoreName;
  dbName = environment.indexDBName;

  constructor() {}

  storeDataInIndexedDB(
    loanOfficers: {
      id: number;
      name: string;
      branchId: number;
      areaId: number;
    }[]
  ): void {
    const dbRequest = indexedDB.open(this.dbName);

    dbRequest.onsuccess = (event: any) => {
      const db = event.target.result;
      const transaction = db.transaction(this.objectStoreName, 'readwrite');
      const objectStore = transaction.objectStore(this.objectStoreName);

      loanOfficers.forEach((branch) => {
        objectStore.put(branch);
      });

      transaction.oncomplete = () => {
        // console.log('loanOfficers stored successfully');
      };

      transaction.onerror = (event) => {
        // console.error('Error storing loanOfficers:', event.target.error);
      };
    };
  }

  getStoredData(): Observable<{ id: number; name: string }[]> {
    const dbRequest = indexedDB.open(this.dbName);

    return new Observable((observer) => {
      dbRequest.onsuccess = (event: any) => {
        const db = event.target.result;
        const transaction = db.transaction(this.objectStoreName, 'readonly');
        const objectStore = transaction.objectStore(this.objectStoreName);
        const request = objectStore.getAll();

        request.onsuccess = (event) => {
          const loanOfficers = (event.target.result as {
            id: number;
            name: string;
          }[]).sort((officerA, officerB) => officerA.name.localeCompare(officerB.name));
          observer.next(loanOfficers);
          observer.complete();
        };

        request.onerror = (event) => {
          console.error('Error retrieving loanOfficers:', event.target.error);
          observer.error(event.target.error);
        };
      };

      dbRequest.onerror = (event: any) => {
        console.error('Error opening database:', event.target.error);
        observer.error(event.target.error);
      };
    });
  }

  getStoredLoanOfficersByBranchId(
    branchId: number
  ): Observable<{ id: number; name: string }[]> {
    if (branchId === null || branchId === undefined) return of([]);
    const dbRequest = indexedDB.open(this.dbName);

    return new Observable((observer) => {
      dbRequest.onsuccess = (event: any) => {
        const db = event.target.result;
        const transaction = db.transaction(this.objectStoreName, 'readonly');
        const objectStore = transaction.objectStore(this.objectStoreName);
        const index = objectStore.index('branchIdIndex');
        const request = index.getAll(IDBKeyRange.only(branchId));

        request.onsuccess = (event) => {
          const loanOfficers = (event.target.result as {
            id: number;
            name: string;
          }[]).sort((officerA, officerB) => officerA.name.localeCompare(officerB.name));
          observer.next(loanOfficers);
          observer.complete();
        };

        request.onerror = (event) => {
          console.error(
            'Error retrieving loanOfficers by branchId:',
            event.target.error
          );
          observer.error(event.target.error);
        };
      };

      dbRequest.onerror = (event: any) => {
        console.error('Error opening database:', event.target.error);
        observer.error(event.target.error);
      };
    });
  }

  getStoredLoanOfficersByAreaId(
    areaId: number
  ): Observable<{ id: number; name: string }[]> {
    if (areaId === null || areaId === undefined) return of([]);
    const dbRequest = indexedDB.open(this.dbName);

    return new Observable((observer) => {
      dbRequest.onsuccess = (event: any) => {
        const db = event.target.result;
        const transaction = db.transaction(this.objectStoreName, 'readonly');
        const objectStore = transaction.objectStore(this.objectStoreName);
        const index = objectStore.index('areaIdIndex');
        const request = index.getAll(IDBKeyRange.only(areaId));

        request.onsuccess = (event) => {
          const loanOfficers = (event.target.result as {
            id: number;
            name: string;
          }[]).sort((officerA, officerB) => officerA.name.localeCompare(officerB.name));
          observer.next(loanOfficers);
          observer.complete();
        };

        request.onerror = (event) => {
          console.error(
            'Error retrieving loanOfficers by areaId:',
            event.target.error
          );
          observer.error(event.target.error);
        };
      };

      dbRequest.onerror = (event: any) => {
        console.error('Error opening database:', event.target.error);
        observer.error(event.target.error);
      };
    });
  }
}
